/**
 * Service de génération de PDF pour les documents génériques
 * 
 * Ce service utilise html2pdf.js pour générer des PDF professionnels
 * à partir des documents génériques de la société
 */

import { getCompanyInfo } from './companyInfoService';
import { db } from '../firebase';

// Les informations de la société peuvent être fournies via les paramètres ou récupérées automatiquement

/**
 * Formate une date de manière sécurisée
 * @param {any} dateValue - Valeur de date (Timestamp, string, Date)
 * @returns {string} Date formatée ou chaîne vide en cas d'erreur
 */
const formatDate = (dateValue) => {
  try {
    if (!dateValue) return '';
    
    let date;
    
    // Gérer les différents types de dates
    if (dateValue.toDate && typeof dateValue.toDate === 'function') {
      // C'est un Timestamp Firebase
      date = dateValue.toDate();
    } else if (dateValue.seconds) {
      // C'est un objet avec seconds (Timestamp Firebase)
      date = new Date(dateValue.seconds * 1000);
    } else if (typeof dateValue === 'string' || typeof dateValue === 'number') {
      // C'est une string ou un timestamp
      date = new Date(dateValue);
    } else {
      // Déjà un objet Date
      date = dateValue;
    }
    
    // Vérifier que la date est valide
    if (isNaN(date.getTime())) {
      return '';
    }
    
    return date.toLocaleDateString('fr-FR');
  } catch (error) {
    console.warn('Erreur de formatage de date:', error);
    return '';
  }
};

/**
 * Génère le HTML d'un document générique pour conversion en PDF
 * @param {Object} document - Données du document générique
 * @param {Object} companyInfo - Informations de la société (doit être fourni)
 * @returns {string} HTML du document
 */
export const genererHTMLDocumentGenerique = async (document, companyInfo) => {
  // Vérifier que les informations de la société sont fournies
  if (!companyInfo) {
    throw new Error('Les informations de la société sont requises pour générer le PDF');
  }

  const currentDate = new Date().toLocaleDateString('fr-FR');

  return `
<!DOCTYPE html>
<html lang="fr">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>${document.documentName}</title>
    <style>
        /* Styles optimisés pour impression professionnelle */
        @page {
            margin: 20mm 15mm 15mm 15mm;
            size: A4;
        }
        
        @media print {
            body {
                font-family: 'Georgia', 'Times New Roman', serif;
                line-height: 1.6;
                color: #2d3748;
                margin: 0;
                padding: 0;
                font-size: 12px;
                background: white;
            }
            
            .container {
                max-width: 170mm;
                margin: 0 auto;
                padding: 0;
            }
            
            /* En-tête professionnel */
            .header {
                text-align: center;
                margin-bottom: 25px;
                padding-bottom: 15px;
                border-bottom: 3px double #3b82f6;
                position: relative;
            }
            
            .header::before {
                content: "";
                position: absolute;
                top: -10px;
                left: 50%;
                transform: translateX(-50%);
                width: 60px;
                height: 3px;
                background: #3b82f6;
            }
            
            .document-title {
                font-size: 24px;
                font-weight: 700;
                color: #1e40af;
                margin-bottom: 8px;
                letter-spacing: 1px;
                text-transform: uppercase;
            }
            
            .document-name {
                font-size: 18px;
                font-weight: 600;
                color: #374151;
                margin-bottom: 12px;
                font-style: italic;
            }
            
            .document-meta {
                font-size: 11px;
                color: #6b7280;
                background: #f8fafc;
                padding: 8px 16px;
                border-radius: 6px;
                display: inline-block;
                border: 1px solid #e2e8f0;
            }
            
            /* Contenu du document professionnel */
            .document-content {
                line-height: 1.7;
                font-size: 12px;
                color: #374151;
            }
            
            .document-content h1 {
                font-size: 18px;
                font-weight: 700;
                color: #1e40af;
                margin: 25px 0 12px 0;
                padding-bottom: 6px;
                border-bottom: 2px solid #3b82f6;
                page-break-after: avoid;
            }
            
            .document-content h2 {
                font-size: 16px;
                font-weight: 600;
                color: #374151;
                margin: 20px 0 10px 0;
                padding-left: 8px;
                border-left: 4px solid #3b82f6;
                page-break-after: avoid;
            }
            
            .document-content h3 {
                font-size: 14px;
                font-weight: 600;
                color: #4b5563;
                margin: 16px 0 8px 0;
                page-break-after: avoid;
            }
            
            .document-content p {
                margin: 12px 0;
                text-align: justify;
                text-indent: 1em;
            }
            
            .document-content p:first-of-type {
                text-indent: 0;
            }
            
            .document-content ul, .document-content ol {
                margin: 12px 0 12px 25px;
            }
            
            .document-content li {
                margin: 6px 0;
                line-height: 1.6;
            }
            
            .document-content ul li::marker {
                color: #3b82f6;
            }
            
            .document-content table {
                width: 100%;
                border-collapse: collapse;
                margin: 16px 0;
                font-size: 11px;
                box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                border-radius: 8px;
                overflow: hidden;
            }
            
            .document-content th {
                background: linear-gradient(135deg, #3b82f6, #1e40af);
                color: white;
                padding: 10px;
                text-align: left;
                font-weight: 600;
                border: none;
                font-size: 11px;
            }
            
            .document-content td {
                padding: 8px 10px;
                border: 1px solid #e2e8f0;
                background: white;
            }
            
            .document-content tr:nth-child(even) td {
                background: #f8fafc;
            }
            
            .document-content .card {
                border: 1px solid #e2e8f0;
                border-radius: 8px;
                padding: 16px;
                margin: 16px 0;
                background: #f8fafc;
                box-shadow: 0 2px 4px rgba(0,0,0,0.05);
            }
            
            .document-content .callout {
                border-left: 4px solid #3b82f6;
                background: linear-gradient(135deg, #f0f9ff, #e0f2fe);
                padding: 12px 16px;
                border-radius: 6px;
                margin: 16px 0;
                font-style: italic;
            }
            
            .document-content blockquote {
                border-left: 4px solid #10b981;
                background: #ecfdf5;
                padding: 12px 16px;
                margin: 16px 0;
                border-radius: 6px;
                font-style: italic;
                color: #065f46;
            }
            
            .document-content code {
                background: #f1f5f9;
                padding: 2px 6px;
                border-radius: 4px;
                font-family: 'Courier New', monospace;
                font-size: 11px;
                color: #dc2626;
            }
            
            .document-content pre {
                background: #1e293b;
                color: #e2e8f0;
                padding: 16px;
                border-radius: 8px;
                margin: 16px 0;
                overflow-x: auto;
                font-family: 'Courier New', monospace;
                font-size: 11px;
                line-height: 1.4;
            }
            
            /* Pied de page professionnel */
            .footer {
                margin-top: 30px;
                padding-top: 12px;
                border-top: 1px solid #e2e8f0;
                font-size: 10px;
                color: #6b7280;
                text-align: center;
            }
            
            .footer::before {
                content: "—";
                margin-right: 8px;
            }
            
            .footer::after {
                content: "—";
                margin-left: 8px;
            }
        }
        
        /* Styles pour écran (prévisualisation) */
        @media screen {
            body {
                font-family: 'Georgia', 'Times New Roman', serif;
                line-height: 1.6;
                color: #2d3748;
                margin: 0;
                padding: 20px;
                font-size: 14px;
                background: #f8fafc;
            }
            
            .container {
                max-width: 210mm;
                margin: 0 auto;
                padding: 30px;
                background: white;
                box-shadow: 0 4px 6px rgba(0,0,0,0.1);
                border-radius: 12px;
            }
            
            .header {
                text-align: center;
                margin-bottom: 30px;
                padding-bottom: 20px;
                border-bottom: 3px double #3b82f6;
                position: relative;
            }
            
            .header::before {
                content: "";
                position: absolute;
                top: -10px;
                left: 50%;
                transform: translateX(-50%);
                width: 60px;
                height: 3px;
                background: #3b82f6;
            }
            
            .document-title {
                font-size: 28px;
                font-weight: 700;
                color: #1e40af;
                margin-bottom: 10px;
                letter-spacing: 1px;
                text-transform: uppercase;
            }
            
            .document-name {
                font-size: 20px;
                font-weight: 600;
                color: #374151;
                margin-bottom: 15px;
                font-style: italic;
            }
            
            .document-meta {
                font-size: 12px;
                color: #6b7280;
                background: #f8fafc;
                padding: 10px 20px;
                border-radius: 8px;
                display: inline-block;
                border: 1px solid #e2e8f0;
            }
        }
        
        /* Utilitaires */
        .page-break {
            page-break-before: always;
        }
        
        .no-break {
            page-break-inside: avoid;
        }
        
        .text-right {
            text-align: right;
        }
        
        .text-center {
            text-align: center;
        }
        
        .font-bold {
            font-weight: bold;
        }
        
        .text-primary {
            color: #3b82f6;
        }
        
        .bg-light {
            background: #f8fafc;
        }
    </style>
</head>
<body>
    <div class="container">
        <!-- En-tête professionnel -->
        <div class="header no-break">
            <div class="document-title">DOCUMENT PROFESSIONNEL</div>
            <div class="document-name">${document.documentName}</div>
            <div class="document-meta">
                Version : ${document.version || '1.0'} | 
                Date : ${currentDate}
                ${document.metadata?.updatedAt ? ` | Dernière modification : ${formatDate(document.metadata.updatedAt)}` : ''}
            </div>
        </div>

        <!-- Contenu du document -->
        <div class="document-content">
            ${document.content}
        </div>
        
        <!-- Pied de page -->
        <div class="footer no-break">
            Document généré le ${currentDate} - ${companyInfo.denominationSociale}
        </div>
    </div>
</body>
</html>
  `;
};

/**
 * Génère et télécharge un document générique en PDF
 * @param {Object} document - Données du document générique
 * @param {Object} options - Options de génération
 */
export const genererPDFDocumentGenerique = async (document, options = {}) => {
  try {
    console.log('🚀 Début de la génération PDF - Version compacte');
    
    // Vérifier si html2pdf est disponible
    if (typeof window === 'undefined') {
      throw new Error('html2pdf n\'est disponible que côté client');
    }

    // Importer html2pdf dynamiquement
    const html2pdf = await import('html2pdf.js');
    
    // Récupérer les informations de la société (depuis les options ou automatiquement)
    let companyInfo = options.companyInfo;
    if (!companyInfo) {
      console.log('📋 Récupération automatique des informations de la société');
      try {
        companyInfo = await getCompanyInfo(db);
        console.log('✅ Informations société récupérées automatiquement');
      } catch (error) {
        console.error('❌ Impossible de récupérer les informations société:', error);
        throw new Error('Les informations de la société sont requises. Fournissez-les via options.companyInfo');
      }
    }
    
    // Générer le HTML du document
    const htmlContent = await genererHTMLDocumentGenerique(document, companyInfo);
    console.log('📄 HTML généré avec styles compacts');
    
    // Vérifier que document est disponible et créer un élément temporaire
    let element;
    if (typeof document !== 'undefined' && document.createElement) {
      element = document.createElement('div');
      element.innerHTML = htmlContent;
    } else {
      // Fallback: utiliser une approche alternative si document n'est pas disponible
      // Créer un élément virtuel en utilisant DOMParser si disponible
      if (typeof DOMParser !== 'undefined') {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');
        element = doc.body;
      } else {
        // Dernier recours: utiliser directement le HTML comme source
        // html2pdf peut accepter du HTML directement dans certains cas
        element = htmlContent;
      }
    }
    
    // Options de configuration pour html2pdf - Version professionnelle standard
    const timestamp = new Date().getTime(); // Timestamp pour éviter le cache
    const pdfOptions = {
      margin: [15, 15, 15, 15], // Marges standard professionnelles
      filename: `${document.documentName}_${companyInfo.sigle}_${timestamp}.pdf`,
      image: { type: 'jpeg', quality: 0.98 }, // Qualité optimale
      html2canvas: { 
        scale: 2,
        useCORS: true,
        logging: false, // Désactivation des logs pour production
        // Améliorations pour éviter les coupures de page
        scrollY: 0,
        windowHeight: element.scrollHeight || 1123, // Hauteur A4 en pixels
        onclone: function(clonedDoc) {
          console.log('🎨 Application des styles de gestion des sauts de page');
          
          // Seulement ajouter les styles pour éviter les coupures, sans toucher au reste
          const style = clonedDoc.createElement('style');
          style.textContent = `
            /* Styles uniquement pour la gestion des sauts de page */
            .page-break-avoid {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            .page-break-before {
              page-break-before: always;
              break-before: page;
            }
            .page-break-after {
              page-break-after: always;
              break-after: page;
            }
            p, h1, h2, h3, h4, h5, h6 {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            table, figure {
              page-break-inside: avoid;
              break-inside: avoid;
            }
          `;
          clonedDoc.head.appendChild(style);
          console.log('✅ Styles de gestion des sauts appliqués');
        }
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait' 
      },
      // Configuration pour éviter les coupures de contenu
      pagebreak: { 
        mode: ['avoid-all', 'css', 'legacy'],
        before: '.page-break-before',
        after: '.page-break-after',
        avoid: '.page-break-avoid'
      },
      ...options.pdfOptions
    };
    
    console.log('⚙️ Configuration PDF appliquée:', {
      margins: pdfOptions.margin,
      quality: pdfOptions.image.quality,
      filename: pdfOptions.filename
    });
    
    // Vérifier si on doit retourner un Blob au lieu de télécharger
    if (options.returnBlob) {
      // Générer le PDF et retourner le Blob
      const pdfBlob = await html2pdf.default()
        .set(pdfOptions)
        .from(element)
        .output('blob');
      
      console.log('✅ PDF généré avec succès (Blob)');
      return pdfBlob;
    } else {
      // Comportement par défaut : télécharger le PDF
      await html2pdf.default()
        .set(pdfOptions)
        .from(element)
        .save();
      
      console.log('✅ PDF généré et téléchargé avec succès');
      return true;
    }
  } catch (error) {
    console.error('❌ Erreur lors de la génération du PDF:', error);
    throw error;
  }
};


/**
 * Retourne l'URL d'un PDF généré (pour prévisualisation)
 * @param {Object} document - Données du document générique
 * @param {Object} options - Options de génération
 * @returns {Promise<string>} URL du PDF
 */
export const genererURLPDFDocumentGenerique = async (document, options = {}) => {
  try {
    // Vérifier si html2pdf est disponible
    if (typeof window === 'undefined') {
      throw new Error('html2pdf n\'est disponible que côté client');
    }

    // Importer html2pdf dynamiquement
    const html2pdf = await import('html2pdf.js');
    
    // Générer le HTML du document
    const htmlContent = await genererHTMLDocumentGenerique(document, options.companyInfo);
    
    // Vérifier que document est disponible et créer un élément temporaire
    let element;
    if (typeof document !== 'undefined' && document.createElement) {
      element = document.createElement('div');
      element.innerHTML = htmlContent;
    } else {
      // Fallback: utiliser une approche alternative si document n'est pas disponible
      // Créer un élément virtuel en utilisant DOMParser si disponible
      if (typeof DOMParser !== 'undefined') {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlContent, 'text/html');
        element = doc.body;
      } else {
        // Dernier recours: utiliser directement le HTML comme source
        // html2pdf peut accepter du HTML directement dans certains cas
        element = htmlContent;
      }
    }
    
    // Options de configuration pour html2pdf - Version compacte
    const pdfOptions = {
      margin: [5, 5, 5, 5], // Marges réduites [top, right, bottom, left]
      image: { type: 'jpeg', quality: 0.95 }, // Qualité légèrement réduite pour un fichier plus petit
      html2canvas: { 
        scale: 2,
        useCORS: true,
        logging: false,
        // Améliorations pour éviter les coupures de page
        scrollY: 0,
        windowHeight: element.scrollHeight || 1123, // Hauteur A4 en pixels
        onclone: function(clonedDoc) {
          // Appliquer des styles supplémentaires pour éviter les coupures
          const style = clonedDoc.createElement('style');
          style.textContent = `
            .page-break-avoid {
              page-break-inside: avoid !important;
              break-inside: avoid !important;
            }
            .page-break-before {
              page-break-before: always !important;
              break-before: page !important;
            }
            .page-break-after {
              page-break-after: always !important;
              break-after: page !important;
            }
            p, h1, h2, h3, h4, h5, h6 {
              page-break-inside: avoid;
              break-inside: avoid;
            }
            table, figure {
              page-break-inside: avoid;
              break-inside: avoid;
            }
          `;
          clonedDoc.head.appendChild(style);
        }
      },
      jsPDF: { 
        unit: 'mm', 
        format: 'a4', 
        orientation: 'portrait' 
      },
      // Configuration pour éviter les coupures de contenu
      pagebreak: { 
        mode: ['avoid-all', 'css', 'legacy'],
        before: '.page-break-before',
        after: '.page-break-after',
        avoid: '.page-break-avoid'
      },
      ...options.pdfOptions
    };
    
    // Générer le PDF et retourner l'URL
    const pdf = await html2pdf.default()
      .set(pdfOptions)
      .from(element)
      .output('bloburl');
    
    return pdf;
  } catch (error) {
    console.error('Erreur lors de la génération de l\'URL PDF:', error);
    throw error;
  }
};

/**
 * Ouvre le document dans une nouvelle fenêtre pour impression
 * @param {Object} document - Données du document générique
 * @param {Object} options - Options d'impression
 */
export const imprimerDocumentGenerique = async (document, options = {}) => {
  try {
    // Récupérer les informations de la société (depuis les options ou automatiquement)
    let companyInfo = options.companyInfo;
    if (!companyInfo) {
      try {
        companyInfo = await getCompanyInfo(db);
      } catch (error) {
        console.error('❌ Impossible de récupérer les informations société:', error);
        throw new Error('Les informations de la société sont requises. Fournissez-les via options.companyInfo');
      }
    }
    
    // Générer le HTML du document
    const htmlContent = await genererHTMLDocumentGenerique(document, companyInfo);
    
    // Créer une nouvelle fenêtre pour l'impression
    const printWindow = window.open('', '_blank');
    printWindow.document.write(htmlContent);
    printWindow.document.close();
    
    // Attendre que le contenu soit chargé puis imprimer
    printWindow.onload = () => {
      printWindow.print();
      // Fermer la fenêtre après impression (optionnel)
      if (options.autoClose !== false) {
        setTimeout(() => printWindow.close(), 500);
      }
    };
    
    return true;
  } catch (error) {
    console.error('Erreur lors de l\'impression:', error);
    throw error;
  }
};

export default {
  genererHTMLDocumentGenerique,
  genererPDFDocumentGenerique,
  imprimerDocumentGenerique,
  genererURLPDFDocumentGenerique
};
